import _ from "underscore";

import { 
	SYNC_RELEVANCY_LEFT, 
	SYNC_RELEVANCY_RIGHT, 
	SET_CURRENT_MAP_ID,
	SET_FILTER_BY_MAP_COUNTRY,
} from "actions/constants.js";

import {
	fetchMapDataCompare,
	fetchTimeseriesCompare,
	fetchCompareArticles,
	fetchEntityFilters,
} from "actions/fetch";

import { 
	addOrRemove,
	mergeEntityFilters,
} from "@exovera/helpers";

import { 
	activateProposedFilters, 
	reactivateFilters,
	genCountryFilter,
	hasCountryFilters,
	removeSourceCountryFilters,
	removeStateFilters,
	getCountryFilters,
} from "lib/helpers/filterHelpers.js";

import { 
	alignGraphDataTypeByPerspective, 
	makeFetchKey 
} from "lib/helpers/helpers.js";

import { getCompareStateId } from "actions/constants/stateIds.js";

const CONTENT_PAGE_LENGTH = 50;

export function updateDataCompareFromState(side) {
	const stateId = getCompareStateId(side);
	return (dispatch, getState) => {
		const { varStore } = getState().storage;
		const varStoreIndex = side.toLowerCase();
		const { 
			activeNarrativeData,
			perspective, 
		} = (side === "Left" ? getState().leftCompare : getState().rightCompare);
		const { 
			currentFiltersList,
			currentZoom,
		} = getState().search[stateId];

		const params = {
			side,
			narrative: activeNarrativeData,
			perspective,
			currentZoom,
			entityFilters: varStore[varStoreIndex][perspective].entityFilters,
			activeFilters: currentFiltersList,
			currentFilters: currentFiltersList,
			mapHighlightId: null,
			contentFilteredByDate: varStore[varStoreIndex][perspective].contentFilteredByDate,
		}

		dispatch(updateDataCompare(params))
	}
}

// one action to update all data for the Narratives tab
//		Updates Map, Graph, Article, and Entities data
// 		Not for MOEs
export function updateDataCompare(parameters) {
	const {
		side,
		activeFilters,
		contentFilteredByDate,
		currentZoom,
		entityFilters,
		eventMode,
		mapHighlightId,
		narrative,
		perspective,
	} = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log("UD Compare =>", narrative);
	const narrativeCopy = JSON.parse(JSON.stringify(narrative));
	const stateId = getCompareStateId(side);

	return (dispatch, getState) => {
		// The base filters are clean
		// const baseFilters = perspective === "Source" ? getState().base.sourceFilters : getState().base.audienceFilters;

		const {
			articlesPageOffset,
			contentHeaderSortTypeSource,
			contentHeaderSortTypeAudience,
			contentHeaderSortAscendingSource,
			contentHeaderSortAscendingAudience,
			dmaAggregation,
			graphDataTypeActive,
			showDuplicates,
			relevancy,
			filterByMapCountry,
			currentMapCountryFilter,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare;
		
		const {
			activeSearchObj,
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			currentEntityType,
			currentFiltersList,
			filters, 
		} = getState().search[stateId];

		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		const { compareMultiSelect, graphDataTypes, hideMap, showTopicsInGraph } = getState().projectConfig;

		// Set the sort variables by perspective
		const contentHeaderSortType = perspective === "Source" ? contentHeaderSortTypeSource : contentHeaderSortTypeAudience;
		const contentHeaderSortAscending = perspective === "Source" ? contentHeaderSortAscendingSource : contentHeaderSortAscendingAudience;

		const numberOfLinesWithParent = narrative.children && narrative.children.length > 0 ? narrative.children.length + 1 : 1;
		const numberOfLinesWithoutParent = narrative.children && narrative.children.length > 0 ? narrative.children.length : 1;
		// Show parent timeseries if showTopicsInGraph or there are no children
		// Also, dont' fetch the base object
		const showTopicLine = (showTopicsInGraph && narrative.parent !== "self") || !narrative.children || narrative.children.length < 1;

		const alignedDataType = alignGraphDataTypeByPerspective({ perspective, graphDataTypeActive, graphDataTypes });

		const fetchKey = makeFetchKey(narrativeCopy, perspective, activeFilters, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		const getChildren = !compareMultiSelect || !narrativeCopy.compositeNarratives || narrativeCopy.compositeNarratives.length <= 1;

		// prepare state
		dispatch({
			type: eventMode ? `updateEventVariables${side}` : `updateVariables${side}`, // ONLY updates varStore
			narrative: narrativeCopy,
			perspective,
			activeFilters,
			entityFilters,
			currentZoom,
			mapHighlightId,
			contentFilteredByDate,
		});

		// Check if map country filtering should be disabled based on country filter configuration
		if (filterByMapCountry) {
			const activeCountryFilters = getCountryFilters(activeFilters);
			let foundInvalidCountry = false;
			for (let i=0; i<activeCountryFilters.length; i++) {
				if (currentMapCountryFilter !== activeCountryFilters[i].id) {
					foundInvalidCountry = true;
					break;
				}
			}
			if (foundInvalidCountry || activeCountryFilters.length === 0) {
				dispatch({
					type: `${SET_FILTER_BY_MAP_COUNTRY}_${side}`,
					value: false
				});
			}
		}

		// Request time series data
		dispatch({
			type: `requestTimeseries${side}`,
			numberOfLines: getChildren
				? showTopicsInGraph && narrativeCopy.parent !== "self"
					? numberOfLinesWithParent
					: numberOfLinesWithoutParent
				: 1,
			narrative: narrativeCopy,
		});

		// Request Articles
		dispatch({ type: `requestArticles${side}` });

		const startDate = contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin || backUpNarrative.rangeStart;
		const endDate = contentFilteredByDate ? Number(contentFilteredByDate.end) : currentZoom.end || backUpNarrative.rangeEnd;
		const params = {
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			narrative: narrativeCopy,
			perspective,
			filters: filters || [],
			currentFilters: currentFiltersList,
			startDate,
			endDate,
			side,
			notMOE: true,
			query: activeSearchObj.query,
			stateCode: mapHighlightId, // for the state that you want county data for, or false.
			dmaAggregation,
			dataType: alignedDataType,
			fetchKey,
			eventMode,
			relevancy,
			showDuplicates,
		};

		if (!hideMap) {
			dispatch(fetchMapDataCompare(params));
		}

		if (showTopicLine) {
			dispatch(fetchTimeseriesCompare(params));
		}

		// don't allow children if multiple narratives are selected
		if (getChildren) {
			_.each(narrativeCopy.children, (trend) => {
				dispatch(
					fetchTimeseriesCompare({
						...params,
						narrative: trend,
						parent: narrativeCopy,
					})
				);
			});
		}

		const articleParams = params;
		articleParams.sortBy = contentHeaderSortType;
		articleParams.ascending = contentHeaderSortAscending;
		articleParams.pageLength = CONTENT_PAGE_LENGTH;
		articleParams.offset = articlesPageOffset;

		dispatch(fetchCompareArticles(articleParams));
		// No entities in Reaction mode
		if (perspective !== "Audience") {
			dispatch(fetchEntityFilters(stateId, currentEntityType));
		}
		dispatch({
			type: side === "Left" ? SYNC_RELEVANCY_LEFT : SYNC_RELEVANCY_RIGHT,
			payload: { relevancy, activeRelevancy: relevancy },
		});
	};
}

export function updateMapDataCompare(parameters) {
	const {
		side,
		activeSearchTranslationEnabled,
		activeSearchTranslationLanguages,
		contentFilteredByDate,
		currentFiltersList,
		currentZoom,
		entityFilters,
		eventMode,
		keywords,
		mapHighlightId,
		narrative,
		perspective,
		query,
		updatedMapSegment,
	} = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log("UD Compare =>", narrative);
	const narrativeCopy = JSON.parse(JSON.stringify(narrative));
	const stateId = getCompareStateId(side);

	return (dispatch, getState) => {
		// The base filters are clean
		const { dmaAggregation, showDuplicates, relevancy } = side === "Left" ? getState().leftCompare : getState().rightCompare;
		
		const {
			filters, 
		} = getState().search[stateId];

		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		const fetchKey = makeFetchKey(narrativeCopy, perspective, currentFiltersList, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		const mergedFilters = filters;

		const startDate = contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin || backUpNarrative.rangeStart;
		const endDate = contentFilteredByDate
			? contentFilteredByDate.begin // fetch functions add a day automatically
			: currentZoom.end || backUpNarrative.rangeEnd;

		dispatch(
			fetchMapDataCompare({
				activeSearchTranslationEnabled,
				activeSearchTranslationLanguages,
				narrative: narrativeCopy,
				perspective,
				filters: mergedFilters || [],
				currentFilters: currentFiltersList,
				dmaAggregation,
				startDate,
				endDate,
				side,
				notMOE: true,
				stateCode: mapHighlightId, // for the state that you want county data for, or false.
				fetchKey,
				eventMode,
				relevancy,
				showDuplicates,
				currentMapSegmentSpan: updatedMapSegment,
				query
			})
		);
	};
}

export function updateCurrentMapDataCompare({data, side}) {
	const stateId = getCompareStateId(side);
	return (dispatch, getState) => {
		const { location } = getState().router;
		const {
			activeKeywords,
			activeNarrativeData,
			contentFilteredByDate,
			entityFilters,
			mapHighlightId,
			perspective,
			currentMapSegmentSpan,
		} = data;
		const { 
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			activeSearchObj,
			currentFiltersList,
			currentZoom, 
		} = getState().search[stateId];
		const eventMode = location && location.pathname === "/events";
		return () => dispatch(
			updateMapDataCompare({
				side,
				activeSearchTranslationEnabled,
				activeSearchTranslationLanguages,
				activeFilters: currentFiltersList,
				contentFilteredByDate,
				currentZoom,
				entityFilters,
				eventMode,
				keywords: activeKeywords,
				mapHighlightId,
				narrative: activeNarrativeData,
				perspective,
				query: activeSearchObj.query,
				updatedMapSegment: currentMapSegmentSpan,
			})
		);
	}
}

export function updateGraphDataCompare(parameters) {
	const { dataType, side } = parameters;
	const stateId = getCompareStateId(side);

	return (dispatch, getState) => {
		const {
			activeNarrativeData,
			contentFilteredByDate,
			entityFilters,
			perspective,
			relevancy,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare; // prettier-ignore

		const { 
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			activeSearchObj,
			currentFiltersList,
			currentZoom, 
			filters,
		} = getState().search[stateId];

		// The base filters are clean
		const narrative = JSON.parse(JSON.stringify(activeNarrativeData));

		const { showTopicsInGraph, compareMultiSelect } = getState().projectConfig;

		const numberOfLinesWithParent = narrative.children && narrative.children.length > 0 ? narrative.children.length + 1 : 1;
		const numberOfLinesWithoutParent = narrative.children && narrative.children.length > 0 ? narrative.children.length : 1;
		// Show parent timeseries if showTopicsInGraph or there are no children
		// Also, dont' fetch the base object
		const showTopicLine = (showTopicsInGraph && narrative.parent !== "self") || !narrative.children || narrative.children.length < 1;

		const fetchKey = makeFetchKey(narrative, perspective, currentFiltersList, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		const getChildren = !compareMultiSelect || !narrative.compositeNarratives || narrative.compositeNarratives.length <= 1;

		dispatch({
			type: `requestTimeseries${side}`,
			numberOfLines: getChildren
				? showTopicsInGraph && narrative.parent !== "self"
					? numberOfLinesWithParent
					: numberOfLinesWithoutParent
				: 1,
			narrative,
			noSpinner: true, // disable whole side spinner
		});

		const startDate = contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin;
		const endDate = contentFilteredByDate
			? contentFilteredByDate.begin // fetch functions add a day automatically
			: currentZoom.end;

		const params = {
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			currentFilters: currentFiltersList,
			dataType,
			endDate,
			eventMode: false,
			fetchKey,
			filters: filters || [],
			narrative,
			notMOE: true,
			perspective,
			query: activeSearchObj.query,
			relevancy,
			side,
			startDate,
		};

		if (showTopicLine) {
			dispatch(
				fetchTimeseriesCompare({
					...params
				})
			);
		}

		// don't allow children if multiple narratives are selected
		if (getChildren) {
			_.each(narrative.children, (trend) => {
				dispatch(
					fetchTimeseriesCompare({
						...params,
						narrative: trend,
						parent: narrative,
					})
				);
			});
		}
	};
}

// one action to update all data for the Narratives tab
//		Updates Map and Graph only
export function updateDataMoe(parameters) {
	const { side, activeFilters, entityFilters, mapHighlightId, narrative, perspective } = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log(
	// 	"UD MOE =>",
	// 	side,
	// 	narrative,
	// 	perspective,
	// 	currentZoom,
	// 	activeFilters,
	// 	mapHighlightId
	// );

	return (dispatch, getState) => {
		const { graphDataTypes, hideMap, moeGraphAvailable, moeMapAvailable } = getState().projectConfig;
		const { noMoe } = getState().base;
		const { dmaAggregation, graphDataTypeActive, relevancy } = side === "Left" ? getState().leftCompare : getState().rightCompare;
		const { 
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			activeSearchObj,
			currentFiltersList,
			currentZoom, 
			filters,
		} = getState().search[stateId];

		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		const alignedDataType = alignGraphDataTypeByPerspective({ perspective, graphDataTypeActive, graphDataTypes });

		const fetchKey = makeFetchKey(narrative, perspective, currentFiltersList, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({
			type: `updateMoeVariables${side}`, // ONLY updates varStore
			narrative,
			perspective,
			activeFilters,
			currentZoom,
			entityFilters,
			mapHighlightId,
		});

		if (!hideMap && moeMapAvailable) {
			dispatch(
				fetchMapDataCompare({
					activeSearchTranslationEnabled,
					activeSearchTranslationLanguages,
					narrative,
					perspective,
					filters: filters || [],
					currentFilters: currentFiltersList,
					startDate: currentZoom.begin || backUpNarrative.rangeStart,
					endDate: currentZoom.end || backUpNarrative.rangeEnd,
					query: activeSearchObj.query,
					side,
					notMOE: noMoe || (narrative.parent !== "moe" && narrative.parent !== "MOE"),
					relevancy,
					stateCode: mapHighlightId, // for the state that you want county data for, or false.
					dmaAggregation,
					fetchKey,
				})
			);
		}

		dispatch({
			type: `requestTimeseries${side}`,
			numberOfLines: narrative.children ? narrative.children.length + 1 : 1,
			narrative,
			noSpinner: !moeGraphAvailable,
		});
		if (moeGraphAvailable) {
			dispatch(
				fetchTimeseriesCompare({
					activeSearchTranslationEnabled,
					activeSearchTranslationLanguages,
					narrative,
					perspective,
					dataType: alignedDataType,
					filters: filters || [],
					currentFilters: currentFiltersList,
					startDate: currentZoom.begin || backUpNarrative.rangeStart,
					endDate: currentZoom.end || backUpNarrative.rangeEnd,
					query: activeSearchObj.query,
					side,
					notMOE: noMoe || (narrative.parent !== "moe" && narrative.parent !== "MOE"),
					relevancy,
					stateCode: mapHighlightId, // for the state that you want county data for, or false.
					fetchKey,
					eventMode: false,
				})
			);
			_.each(narrative.children, (trend) => {
				dispatch(
					fetchTimeseriesCompare({
						activeSearchTranslationEnabled,
						activeSearchTranslationLanguages,
						narrative: trend,
						perspective,
						dataType: alignedDataType,
						filters: filters || [],
						currentFilters: currentFiltersList,
						startDate: currentZoom.begin || backUpNarrative.rangeStart,
						endDate: currentZoom.end || backUpNarrative.rangeEnd,
						query: activeSearchObj.query,
						relevancy,
						side,
						notMOE: noMoe || (narrative.parent !== "moe" && narrative.parent !== "MOE"),
						stateCode: mapHighlightId, // for the state that you want county data for, or false.
						fetchKey,
						eventMode: false,
					})
				);
			});
		}
	};
}

// one action to update Articles, Entities, and Maps
//		For use with zooming the graph
// 		Works with 'all'
export function updateLowerDataCompare(parameters) {
	const {
		side,
		activeFilters,
		contentFilteredByDate,
		currentZoom, 
		entityFilters,
		keywords,
		mapHighlightId,
		narrative,
		narType, // leftMOE or left || rightMOE or right
		perspective,
		offset,
		pageLength,
	} = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log("Update Lower Data =>", activeFilters, contentFilteredByDate);
	const stateId = getCompareStateId(side);

	return (dispatch, getState) => {
		const {
			articlesPageOffset,
			contentHeaderSortAscendingAudience,
			contentHeaderSortAscendingSource,
			contentHeaderSortTypeAudience,
			contentHeaderSortTypeSource,
			showDuplicates,
			relevancy,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare;

		const { 
			activeSearchObj,
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			currentEntityType,
			currentFiltersList,
			filters,
		} = getState().search[stateId];
		
		const { hideMap } = getState().projectConfig;

		const { location } = getState().router;
		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		// Set the sort variables by perspective
		const contentHeaderSortType = perspective === "Source" ? contentHeaderSortTypeSource : contentHeaderSortTypeAudience;
		const contentHeaderSortAscending = perspective === "Source" ? contentHeaderSortAscendingSource : contentHeaderSortAscendingAudience;

		const fetchKey = makeFetchKey(narrative, perspective, currentFiltersList, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({
			type: location && location.pathname === "/events" ? "updateLowerEventVariablesCompare" : "updateLowerVariablesCompare", // ONLY updates varStore
			contentFilteredByDate,
			currentZoom,
			perspective,
			narrative,
			narType,
		});
		dispatch({ type: `requestArticles${side}` });


		const params = {
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			currentFilters: currentFiltersList,
			endDate: currentZoom.end || backUpNarrative.rangeEnd,
			fetchKey,
			filters: filters || [],
			narrative,
			notMOE: true,
			perspective,
			query: activeSearchObj.query,
			relevancy,
			showDuplicates,
			side,
			stateCode: mapHighlightId,
			startDate: currentZoom.begin || backUpNarrative.rangeStart,
		};
		const articleParams = params;
		articleParams.sortBy = contentHeaderSortType;
		articleParams.ascending = contentHeaderSortAscending;
		articleParams.pageLength = pageLength !== undefined ? pageLength : CONTENT_PAGE_LENGTH;
		articleParams.offset = offset !== undefined ? offset : articlesPageOffset;
		dispatch(fetchCompareArticles(articleParams));
		// No entities in Reaction mode
		if (perspective !== "Audience") {
			dispatch(fetchEntityFilters(stateId, currentEntityType));
		}

		if (!hideMap) {
			dispatch(fetchMapDataCompare(params));
		}
	};
}

// one action to update Articles and Entities for the Narratives tab
//		For use with updateContentByDate. Stores the indicator (or clears it)
// 		Works with 'all'
export function updateDataByDateCompare(parameters) {
	const {
		contentFilteredByDate,
		currentZoom,
		entityFilters,
		mapHighlightId,
		narrative,
		narType,
		perspective,
		side,
		offset,
		pageLength,
	} = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log("Update Data by date =>", currentZoom, activeFilters, contentFilteredByDate);
	const stateId = getCompareStateId(side);

	return (dispatch, getState) => {
		const {
			articlesPageOffset,
			contentHeaderSortAscendingAudience,
			contentHeaderSortAscendingSource,
			contentHeaderSortTypeAudience,
			contentHeaderSortTypeSource,
			showDuplicates,
			relevancy,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare;

		const { 
			activeSearchObj,
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			currentEntityType,
			currentFiltersList,
			filters,
		} = getState().search[stateId];

		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		// Set the sort variables by perspective
		const contentHeaderSortType = perspective === "Source" ? contentHeaderSortTypeSource : contentHeaderSortTypeAudience;
		const contentHeaderSortAscending = perspective === "Source" ? contentHeaderSortAscendingSource : contentHeaderSortAscendingAudience;

		const fetchKey = makeFetchKey(narrative, perspective, currentFiltersList, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({
			type: "updateVariablesByDateCompare", // ONLY updates varStore
			narrative,
			perspective,
			contentFilteredByDate,
			narType,
		});
		dispatch({ type: `requestArticles${side}` });

		const params = {
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			currentFilters: currentFiltersList,
			endDate: contentFilteredByDate ? Number(contentFilteredByDate.end) : currentZoom.end || backUpNarrative.rangeEnd,
			fetchKey,
			filters: filters || [],
			narrative,
			notMOE: true,
			perspective,
			query: activeSearchObj.query,
			relevancy,
			showDuplicates,
			side,
			stateCode: mapHighlightId, // for the state that you want county data for, or false.
			startDate: contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin || backUpNarrative.rangeStart,
		};

		const articleParams = params;
		articleParams.sortBy = contentHeaderSortType;
		articleParams.ascending = contentHeaderSortAscending;
		articleParams.pageLength = pageLength !== undefined ? pageLength : CONTENT_PAGE_LENGTH;
		articleParams.offset = offset !== undefined ? offset : articlesPageOffset;

		dispatch(fetchCompareArticles(articleParams));
		// No entities in Reaction mode
		if (perspective !== "Audience") {
			dispatch(fetchEntityFilters(stateId, currentEntityType));
		}
	};
}

// Updates artices only. Used with the show duplicates toggle
//   NOTE: this doesn't update the varStore, since no tracked variables are changing
export function updateArticleDataCompare(parameters) {
	const { showDuplicates, side } = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	const stateId = getCompareStateId(side);

	return (dispatch, getState) => {
		const narrative = side === "Left" ? getState().leftCompare.activeNarrativeData : getState().rightCompare.activeNarrativeData;
		const {
			contentFilteredByDate,
			contentHeaderSortTypeSource,
			contentHeaderSortTypeAudience,
			contentHeaderSortAscendingSource,
			contentHeaderSortAscendingAudience,
			entityFilters,
			mapHighlightId,
			perspective,
			relevancy,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare;

		const { 
			activeSearchTranslationEnabled,
			activeSearchTranslationLanguages,
			currentFiltersList,
			filters,
			activeSearchObj,
			currentZoom,
		} = getState().search[stateId];



		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		// Set the sort variables by perspective
		const contentHeaderSortType = perspective === "Source" ? contentHeaderSortTypeSource : contentHeaderSortTypeAudience;
		const contentHeaderSortAscending = perspective === "Source" ? contentHeaderSortAscendingSource : contentHeaderSortAscendingAudience;

		const fetchKey = makeFetchKey(narrative, perspective, currentFiltersList, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({ type: `requestArticles${side}` });

		dispatch(
			fetchCompareArticles({
				activeSearchTranslationEnabled,
				activeSearchTranslationLanguages,
				ascending: contentHeaderSortAscending,
				currentFilters: currentFiltersList,
				endDate: contentFilteredByDate
					? contentFilteredByDate.begin // fetch functions add a day automatically
					: currentZoom.end || backUpNarrative.rangeEnd,
				fetchKey,
				filters: filters || [],
				narrative,
				notMOE: true,
				perspective,
				query: activeSearchObj.query,
				relevancy,
				showDuplicates,
				side,
				sortBy: contentHeaderSortType,
				startDate: contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin || backUpNarrative.rangeStart,
				stateCode: mapHighlightId, // for the state that you want county data for, or false.
			})
		);
	};
}

// one action to update Entities
//		For use with the translate endities toggle
// 		Works with 'all'
export function updateEntityDataCompare({
	//  translateEntities,
	side 
}) {
	const stateId = getCompareStateId(side);
	return (dispatch, getState) => {
		const {
			currentEntityType,
		} = getState().search[stateId];
		const { 
			// activeKeywords, 
			activeNarrativeData, 
			contentFilteredByDate, 
			currentZoom, 
			// entityFilters, 
			filtersList, 
			perspective, 
			relevancy 
		} =	side === "Left" ? getState().leftCompare : getState().rightCompare;
			
		const fetchKey = makeFetchKey(activeNarrativeData, perspective, filtersList, currentZoom, relevancy);

		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({
			type: "updateLowerVariablesCompare", // ONLY updates varStore
			contentFilteredByDate,
			currentZoom,
			perspective,
			narrative: activeNarrativeData,
			narType: side.toLowerCase(), // assumes no moeMode, since these features don't currently overlap
		});

		// const params = {
		// 	currentFilters,
		// 	endDate,
		// 	fetchKey,
		// 	filtersList: mergedFilters || [],
		// 	narrative: activeNarrativeData,
		// 	perspective,
		// 	relevancy,
		// 	side,
		// 	startDate,
		// 	translateEntities,
		// };

		// No entities in Reaction mode
		if (perspective !== "Audience") {
			dispatch(fetchEntityFilters(stateId, currentEntityType));
		}
	};
}

export function changeMap(id, stateIdPrefix, side) {
	const stateId = getCompareStateId(side);
	return (dispatch, getState) => {
		const { varStore } = getState().storage;
		const { topicsPie } = getState().projectConfig;
		const { 
			activeNarrativeData,
			currentMapId,
			filterByMapCountry, 
			perspective, 
		} = (side === "Left" ? getState().leftCompare : getState().rightCompare);
		const { 
			currentFiltersList,
			currentZoom,
		} = getState().search[stateId];
		const varStoreIndex = side.toLowerCase();

		dispatch({
			type: `${SET_CURRENT_MAP_ID}_${side}`,
			id,
			stateIdPrefix,
		});

		if (filterByMapCountry) {
			const filterObject = genCountryFilter({ 
				countryId: id, 
				active: filterByMapCountry, 
			});
			
			let newFilters = Array.isArray(currentFiltersList) ? currentFiltersList : [];
			newFilters = removeSourceCountryFilters(newFilters);
			newFilters = addOrRemove(newFilters, filterObject, "id");
			newFilters = removeStateFilters(newFilters, stateIdPrefix);

			dispatch(
				updateDataCompare({
					side,
					narrative: activeNarrativeData,
					perspective,
					currentZoom,
					entityFilters: varStore[varStoreIndex][perspective].entityFilters,
					activeFilters: newFilters,
					currentFilters: currentFiltersList,
					mapHighlightId: null,
					contentFilteredByDate: varStore[varStoreIndex][perspective].contentFilteredByDate,
					topicsPie,
				})
			);
		}
	};
}

export const toggleFilterByMapCountry = (side) => {
	const stateId = getCompareStateId(side);
	return (dispatch, getState) => {

		const { varStore } = getState().storage;
		const { topicsPie } = getState().projectConfig;
		const { 
			activeNarrativeData,
			currentMapId,
			filtersList,
			filterByMapCountry, 
			perspective, 
			currentMapStateIdPrefix,
		} =	(side === "Left" ? getState().leftCompare : getState().rightCompare);

		const { 
			currentFiltersList,
			currentZoom,
		} = getState().search[stateId];

		const varStoreIndex = side.toLowerCase();

		const newState = !filterByMapCountry;
		const filterObject = genCountryFilter({ 
			countryId: currentMapId, 
			active: newState, 
		});

		let newFilters = Array.isArray(currentFiltersList) ? currentFiltersList : [];

		// If any country filters are set and changing to not filter by
		// displayed map country then clear all active country filters
		if (hasCountryFilters(newFilters) && !newState) {
			newFilters = removeSourceCountryFilters(newFilters);
		}

		// If any country filters are set and changing to filter by
		// displayed map country then remove all current country filters 
		// and replace with a single new country filter for the active map.
		// Also remove any state/province level filters set which are not
		// relevant to the current map country.
		else if (hasCountryFilters(newFilters)) {
			newFilters = removeSourceCountryFilters(newFilters);
			newFilters = addOrRemove(newFilters, filterObject, "id");
			newFilters = removeStateFilters(newFilters, currentMapStateIdPrefix);
		}

		// If no country filters are set and changing to filter by displayed
		// country then add a single new country filter for the active map
		// Also remove any state/province level filters set which are not
		// relevant to the current map country.
		else if (!hasCountryFilters(newFilters) && newState) {
			newFilters = addOrRemove(newFilters, filterObject, "id");
			newFilters = removeStateFilters(newFilters, currentMapStateIdPrefix);
		}

		// Should never be here (no filters set + moving out of a filtered
		// map state), just clear any active country filters
		else {
			newFilters = removeSourceCountryFilters(newFilters);
		}

		// Store changes & send API updates
		dispatch({
			type: `${SET_FILTER_BY_MAP_COUNTRY}_${side}`,
			value: newState,
		});

		dispatch(
			updateDataCompare({
				side,
				narrative: activeNarrativeData,
				perspective,
				currentZoom,
				entityFilters: varStore[varStoreIndex][perspective].entityFilters,
				activeFilters: newFilters,
				mapHighlightId: null,
				contentFilteredByDate: varStore[varStoreIndex][perspective].contentFilteredByDate,
				topicsPie,
			})
		);
	};
};